import { pd, dlb, cbd, msa } from 'neuro-schemas';
import { sortPartialDate } from 'neuro-utils';

const {
  utils: { clinicallyEstablishedPD, probablePD },
} = pd;

const {
  utils: { probableDLB, possibleDLB },
} = dlb;

const {
  utils: { ProbableCBDCanBeDiagnosed, PossibleCBDCanBeDiagnosed },
} = cbd;

const {
  utils: {
    checkDiagnosticCriteriaForClinicallyEstablishedMSA,
    checkDiagnosticCriteriaForClinicallyProbableMSA,
    possibleMSA,
    probableMSA,
  },
} = msa;

/**
 * Return latest diagnosis document from documents, based on type(s)
 * @param  {string|string[]} type - Type of diagnosis
 * @param  {Array<IDocumentWithTypeV2<'diagnosis'>>[]} documents - Diagnosis documents
 * @returns {IDocumentWithTypeV2<'diagnosis'>} Diagnosis document
 */
export const lastDiagnosis = (
  type: string | Array<string | undefined>,
  documents: Array<IDocumentWithTypeV2<'diagnosis'>>,
): IDiagnosis => {
  const arr = typeof type === 'string' ? [type] : type;
  return (
    documents.filter(d => arr.includes(d.diagnosis ?? '')).sort((n1, n2) => sortPartialDate(n2.date, n1.date))[0] ?? {}
  );
};

/**
 * Determines the diagnostic criteria result for a given document in priority order.
 * For types that support it, checks in "waterfall" fashion: clinicallyEstablished → probable → possible → no → unknown → undefined.
 * For others, the highest available tier is used.
 *
 * @param d - Diagnostic criteria document; its `_type` selects which criteria are applied
 * @returns {'clinicallyEstablished' | 'probable' | 'possible' | 'no' | 'unknown' | undefined} The first matching result, or `undefined` if type not supported.
 */
export const clinicallyEstablishedOrProbableOrPossible = (
  d: IDocumentWithTypeV2<'pd' | 'msa' | 'dlb' | 'cbd'>,
): 'clinicallyEstablished' | 'probable' | 'possible' | 'no' | 'unknown' | undefined => {
  switch (d._type) {
    case 'pd':
      if (clinicallyEstablishedPD(d) === 'yes') return 'clinicallyEstablished';
      if (probablePD(d) === 'yes') return 'probable';
      if (clinicallyEstablishedPD(d) === 'no' || probablePD(d) === 'no') return 'no';
      return 'unknown';
    case 'msa': {
      if (d.version !== '2022') {
        if (probableMSA(d) === 'yes') return 'probable';
        if (possibleMSA(d) === 'yes') return 'possible';
        if (probableMSA(d) === 'no' || possibleMSA(d) === 'no') return 'no';
        return 'unknown';
      }
      if (checkDiagnosticCriteriaForClinicallyEstablishedMSA(d) === 'yes') return 'clinicallyEstablished';
      if (checkDiagnosticCriteriaForClinicallyProbableMSA(d) === 'yes') return 'probable';
      if (
        checkDiagnosticCriteriaForClinicallyEstablishedMSA(d) === 'no' ||
        checkDiagnosticCriteriaForClinicallyProbableMSA(d) === 'no'
      )
        return 'no';
      return 'unknown';
    }
    case 'dlb':
      if (probableDLB(d) === 'yes') return 'probable';
      if (possibleDLB(d) === 'yes') return 'possible';
      if (probableDLB(d) === 'no' || possibleDLB(d) === 'no') return 'no';
      return 'unknown';
    case 'cbd':
      if (ProbableCBDCanBeDiagnosed(d) === 'yes') return 'probable';
      if (PossibleCBDCanBeDiagnosed(d) === 'yes') return 'possible';
      if (ProbableCBDCanBeDiagnosed(d) === 'no' || PossibleCBDCanBeDiagnosed(d) === 'no') return 'no';
      return 'unknown';
    default:
      return undefined;
  }
};
